import React, { useEffect } from 'react';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import FavoriteIcon from '@material-ui/icons/Star';
import RecentIcon from '@material-ui/icons/AccessTime';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useAsync } from 'react-async-hook';
import { batch } from 'react-redux';
import useConstant from 'use-constant';
import CoreAPIService from '../../utils/core-api';
import FormUtils from '../../utils/forms';
import ObjectUtils from '../../utils/objectUtils';
import ErrorMessage from '../error-message';
import MainLoading from '../main-loading';
import { RiderFavorites } from '../../redux/reducers/api-types';
import AddressUtils from '../../utils/addressUtils';
import { API_QUERY_PLACEHOLDER } from '../../utils/constants';
import envConfig from '../../config';

interface AutoFillType {
    id: string;
    label: string;
    api: string;
    minChar?: number; // Minimum of character to launch the get request
    name?: string;
    required?: boolean;
    autofocus?: boolean;
    onChange?: any;
    onFind?: any;
    location?: boolean;
    locationNoDetails?: boolean;
    favorites?: RiderFavorites;
    formData?: any;
    filter?: AutoFillFilter;
    filterResult?: Function;
    customDatalist?: Function;
    loaderPosition?: 'top' | 'left' | 'right' | 'bottom';
    loaderProps?: any;
    style?: React.CSSProperties;
    inputParams?: any;
    variant?: 'filled' | 'standard' | 'outlined';
    defaultValue?: AutoFillDefault;
    customAlt?: Function;
    isUuid?: boolean;
}

interface AutoFillDefault {
    id: number;
    name: string;
}

interface AutoFillFilter {
    test: Function;
    existingList: Array<any>;
}

interface AutoFillProps {
    onRef?: any;
    formControl?: boolean;
    shortLabel?: boolean;
    disabled?: boolean;
    datas: AutoFillType;
}

//= ============//
//= LeCab Géo =//
//= ============//

interface Address {
    text: string;
    vicinity: string;
}

interface Poi {
    location: number[];
    name: string;
    place_id: string;
    distance: number;
    address: Address;
    categories: any[];
}

enum FavoriteType {
    FAVORITE = 'favorite',
    RECENT = 'recent',
}

const AutoFill = (props: AutoFillProps): JSX.Element => {
    const classes = useStyles();
    const {
        formControl = true,
        shortLabel = false,
        disabled = false,
        onRef,
        datas: {
            id,
            label,
            name,
            required,
            api,
            autofocus,
            onChange,
            onFind,
            location,
            locationNoDetails,
            favorites,
            style,
            customDatalist,
            filterResult,
            minChar = 1,
            formData,
            filter: { existingList, test } = { existingList: null, test: null },
            loaderPosition,
            loaderProps,
            inputParams,
            variant,
            defaultValue,
            customAlt,
            isUuid = false,
        },
    } = props;

    const [selectedId, setSelectedId] = React.useState<any>(defaultValue?.id ?? -1);
    const [selectedName, setSelectedName] = React.useState<string>('');
    const [selectedItem, setSelectedItem] = React.useState<any>(null);
    const [isLoadingDetails, setLoadingDetails] = React.useState<boolean>(false);
    const [favoritePlaces, setFavoritePlaces] = React.useState<any[]>([]);

    useEffect(() => {
        if (onRef) {
            onRef({ reset: resetField, fieldInfos: getFieldInfos });
        }
        return () => {
            if (onRef) {
                onRef(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRef]);

    useEffect(() => {
        if (favorites?.favorites || favorites?.recent) {
            const res = [];
            favorites.favorites.forEach((f) => {
                const { address } = f.location;
                if (address) {
                    res.push({
                        type: FavoriteType.FAVORITE,
                        address: {
                            text: AddressUtils.favoritesToAddress(address),
                            label: f.name,
                            ...address,
                        },
                        location: [f.location.position.lat, f.location.position.lng],
                    });
                }
            });
            favorites.recent.forEach((f) => {
                const { address } = f;
                if (address) {
                    res.push({
                        type: FavoriteType.RECENT,
                        address: {
                            text: AddressUtils.favoritesToAddress(address),
                            ...f.address,
                        },
                        location: [f.position.lat, f.position.lng],
                    });
                }
            });
            setFavoritePlaces(res);
        }
    }, [favorites]);

    const searchAPI = (text, searchActivated): Promise<any> => {
        let apiUrl = api;
        if (!isNaN(text as any)) {
            apiUrl = api.replace('[name]', '[id]');
        }
        return searchActivated && text.length >= minChar
            ? apiUrl.startsWith('http')
                ? CoreAPIService.sendRequest(
                      apiUrl.replace(API_QUERY_PLACEHOLDER, encodeURIComponent(text)),
                      { method: 'GET' },
                      apiUrl.startsWith(envConfig.id_url) ? { credential: true } : null,
                      true,
                  )
                : CoreAPIService.get(apiUrl.replace(API_QUERY_PLACEHOLDER, encodeURIComponent(text)))
            : Promise.resolve({});
    };

    const detailAPI = (place_id: string): Promise<any> =>
        CoreAPIService.sendRequest(
            `${envConfig.lecab_geo.url}/places/details.json?format=json&place_id=${encodeURIComponent(place_id)}&app_key=${
                envConfig.lecab_geo.key
            }&app_code=${envConfig.lecab_geo.code}`,
            { method: 'GET' },
            null,
            true,
        );

    const useSearch = () => {
        // Handle the input text state
        const [inputText, setInputText] = React.useState<string>(defaultValue?.name ?? '');
        const [searchActivated, setSearchActivated] = React.useState<Boolean>(true);

        // Debounce the original search async function
        const debouncedSearch = useConstant(() => AwesomeDebouncePromise(searchAPI, 500));

        // Simply use it with useAsync
        const search: any = useAsync(debouncedSearch, [inputText, searchActivated]);

        if (api.startsWith('/ticketing/') && search.result?.payload) {
            const dataKey = Object.keys(search.result.payload).filter((t) => t !== 'total')[0];
            search.result.payload.data = search.result.payload[dataKey];
        } else if (search.result?.payload && !search.result?.payload?.data && Array.isArray(search.result?.payload)) {
            search.result.payload.data = search.result.payload;
        }

        // Apply filterResult if exists
        if (filterResult && search.result?.payload?.data) {
            search.result.payload.data = filterResult(search.result.payload.data);
        }

        // Return everything needed for the hook consumer
        return {
            inputText,
            setInputText,
            search,
            searchActivated,
            setSearchActivated,
        };
    };

    const { inputText, setInputText, search, setSearchActivated } = useSearch();

    const resetField = () => {
        batch(() => {
            setInputText('');
            setSelectedId(-1);
            setSelectedName('');
            setSelectedItem(null);
            setSearchActivated(true);
        });
    };

    const getFieldInfos = () => {
        return {
            inputText,
            selectedId,
            selectedName,
            selectedItem,
        };
    };

    const launchSearch = (event): void => {
        const text = event.target.value;

        const regex = /(\((.*?)\) )?(.*)/g;
        const found = regex.exec(text);

        const id = found[2] ? (isUuid ? found[2] : parseInt(found[2])) : -1;
        const name = found[3];

        // Check if the event is from typing or selecting in the list.
        const needReload = id === -1;
        let item = search?.result?.payload?.data && search.result.payload.data.find((x) => x.id === id);
        if (search?.result?.payload?.data && onFind) {
            item = onFind(search.result.payload.data, id);
        }

        batch(() => {
            setSearchActivated(needReload);
            setSelectedId(id);
            setSelectedName(name);
            setSelectedItem(item);
            setInputText(name);
        });

        if (onChange) {
            onChange(id, name, item, search, event);
        }
        if (formData) {
            FormUtils.nextSubState(event, formData);
        }
    };

    const launchPOISearch = (event): void => {
        const name: string = event.target.value;
        setInputText(name);
    };

    const onChangePOI = (event, value, reason): void => {
        if (reason === 'clear') {
            resetField();
            if (onChange) {
                onChange({ target: { id, name: id, value: '' } });
            }
        }
        if (event.target.id) {
            if (!locationNoDetails && value?.place_id) {
                event.persist();
                setLoadingDetails(true);
                detailAPI(value.place_id)
                    .then((res) => {
                        const detail = res.payload?.results?.[0];
                        if (onChange) {
                            onChange(event, detail);
                        }
                        if (formData) {
                            ObjectUtils.setSubValueFromKey(formData, event.target.id.split('-option')[0], detail);
                        }
                    })
                    .catch((e) => {
                        console.error('Error during lecabgeo detail:', e);

                        if (onChange) {
                            onChange(event, value);
                        }
                        if (formData) {
                            ObjectUtils.setSubValueFromKey(formData, event.target.id.split('-option')[0], value);
                        }
                    })
                    .finally(() => {
                        setLoadingDetails(false);
                    });
            } else {
                if (onChange) {
                    onChange(event, value);
                }
                if (formData) {
                    ObjectUtils.setSubValueFromKey(formData, event.target.id.split('-option')[0], value);
                }
            }
        }
    };

    if (location) {
        return (
            <Autocomplete
                id={id}
                style={{ width: '45%', ...style }}
                getOptionLabel={(option: Poi) => option.address.text}
                filterOptions={(x) => x}
                options={search?.result?.payload?.results ?? favoritePlaces}
                autoComplete
                includeInputInList
                loading={search.loading || isLoadingDetails}
                onChange={onChangePOI}
                disabled={disabled}
                // clearOnBlur ==> false TODO
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        name={id}
                        required={required ?? true}
                        fullWidth
                        variant={variant ?? 'standard'}
                        onChange={launchPOISearch}
                    />
                )}
                renderOption={(option) => {
                    const style = { marginRight: 8 };
                    let icon = <LocationOnIcon style={style} />;
                    if (option?.type === FavoriteType.FAVORITE) {
                        icon = <FavoriteIcon style={style} />;
                    } else if (option?.type === FavoriteType.RECENT) {
                        icon = <RecentIcon style={style} />;
                    }
                    return (
                        <Grid container alignItems="center" id={id}>
                            <Grid item>{icon}</Grid>
                            <Grid item xs>
                                <Typography variant="body2" color="textSecondary" id={id}>
                                    {`${option.address.label ? `${option.address.label} - ` : ''}${
                                        option.address.text
                                    }`}
                                </Typography>
                            </Grid>
                        </Grid>
                    );
                }}
            />
        );
    }

    const loaderError: JSX.Element = (
        <div>
            {search.loading && <MainLoading {...loaderProps} />}
            <ErrorMessage payload={search.error} />
        </div>
    );

    const horizontal = loaderPosition === 'right' || loaderPosition === 'left';
    const isInputEmpty = inputText.length === 0;
    const isSelected = selectedId === -1;

    let content: JSX.Element = (
        <div className={classes.flexInherit}>
            <TextField
                disabled={disabled}
                autoFocus={autofocus || false}
                style={{ marginLeft: 8, width: '100%', ...style }}
                className={isInputEmpty ? classes.idle : isSelected ? classes.ko : classes.ok}
                error={!isInputEmpty && isSelected}
                id={id}
                label={shortLabel || isSelected ? label : `${label} - (${selectedId}) ${inputText}`}
                name={name ?? id}
                fullWidth
                value={inputText}
                required={required ?? true}
                variant={variant || 'standard'}
                inputProps={{
                    list: `guess_${id}`,
                    autoComplete: 'off',
                    ...inputParams,
                    ...(isSelected || (!selectedItem?.uuid && !selectedItem?.id && !customAlt)
                        ? {}
                        : { alt: customAlt ? customAlt(selectedItem) : selectedItem?.uuid || selectedItem?.id }),
                }}
                onChange={launchSearch}
            />
            <datalist id={`guess_${id}`}>
                {search?.result?.payload?.data &&
                    search.result.payload.data
                        .filter((x) => (existingList ? test(existingList, x) : true))
                        .map((select, i) => {
                            if (customDatalist) {
                                return customDatalist(select);
                            }
                            const keyId = select.id || select?.[id];
                            const fullName =
                                select.fullname ||
                                select.name ||
                                [select.firstname, select.lastname].filter(Boolean).join(' ');
                            const value = `(${keyId}) ${fullName}`;
                            return (
                                <option key={keyId} value={value} data-id={keyId}>
                                    {value}
                                </option>
                            );
                        })}
            </datalist>
        </div>
    );

    if (formControl) {
        content = <FormControl className={classes.flexInherit}>{content}</FormControl>;
    }

    return (
        <div style={horizontal ? { display: 'flex', flexDirection: 'row', flex: 'inherit' } : {}}>
            {(!loaderPosition || loaderPosition === 'top' || loaderPosition === 'left') && loaderError}
            {content}
            {(loaderPosition === 'bottom' || loaderPosition === 'right') && loaderError}
        </div>
    );
};

//= ====================================//
//= ============== Style ===============//
//= ====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        flexInherit: {
            flex: 'inherit',
        },
        ko: {
            // '& .MuiInput-underline:before': {
            //     borderBottomColor: 'rgba(255, 0, 0, 0.42)',
            // },
        },
        ok: {
            '& .MuiFormLabel-root.Mui-focused, & .MuiInputLabel-root': {
                color: 'rgba(0, 197, 50, 0.80)',
            },
            '& .MuiInput-underline:before': {
                borderBottomColor: 'rgba(0, 197, 0, 0.42)',
            },
            '& .MuiInput-underline:after': {
                borderBottomColor: 'rgba(0, 197, 0, 0.42)',
                borderBottomWidth: '2px',
            },
        },
        idle: {
            // '& .MuiInput-underline:before': {
            //   borderBottomColor: 'rgba(255, 0, 0, 0.42)'
            // }
        },
    }),
);

export default AutoFill;
